import type {
  RailwayStatusPayload,
  RailwayProjectContext,
  RailwayRunner
} from '@cli-types/next-adapter/railway.js'
import { outputTail } from '@cli-utils/process.js'
import { isNonEmptyString } from '@cli-utils/shared.js'
import { parseRailwayJson } from '@next-adapter/init/railway/runner/parse-railway-json.js'
import { runRailway } from '@next-adapter/init/railway/runner/run-railway.js'
import { hasNoUsableLinkedProject } from './has-no-usable-linked-project.js'

export async function readRailwayProjectContext(
  runner: RailwayRunner,
  cwd: string,
  environment: string,
  env?: NodeJS.ProcessEnv
): Promise<RailwayProjectContext | undefined> {
  const result = await runRailway(runner, ['status', '--environment', environment, '--json'], {
    cwd,
    mode: 'capture',
    env
  })
  if (!result.success) {
    const detail = outputTail(`${result.stdout}\n${result.stderr}`)
    if (detail && hasNoUsableLinkedProject(detail)) {
      return undefined
    }
    throw new Error(detail ?? 'Could not inspect the linked Railway project.')
  }

  const payload = parseRailwayJson<RailwayStatusPayload>(result.stdout)
  if (!payload || !isNonEmptyString(payload.id) || !isNonEmptyString(payload.name)) {
    throw new Error('Railway returned an invalid project status response.')
  }

  const environments = payload.environments?.edges ?? []
  const selected = environments
    .map((edge) => edge.node)
    .find(
      (candidate) =>
        isNonEmptyString(candidate?.id) &&
        isNonEmptyString(candidate?.name) &&
        (candidate.id.toLowerCase() === environment.toLowerCase() ||
          candidate.name.toLowerCase() === environment.toLowerCase())
    )

  if (!selected || !isNonEmptyString(selected.id) || !isNonEmptyString(selected.name)) {
    throw new Error(
      `Railway project ${payload.name} does not have an environment matching "${environment}".`
    )
  }

  return {
    id: payload.id,
    name: payload.name,
    environmentId: selected.id,
    environmentName: selected.name
  }
}
